"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapToChatMl = mapToChatMl;
exports.mapOutputToChatMl = mapOutputToChatMl;
exports.cleanLegacyOutput = cleanLegacyOutput;
exports.extractAdditionalInput = extractAdditionalInput;
exports.combineInputOutputMessages = combineInputOutputMessages;
exports.normalizeInput = normalizeInput;
exports.normalizeOutput = normalizeOutput;
/**
 * ChatML core normalization functions.
 *
 * NOTE: Moved to shared package to enable testing
 * because web can't import from worker. Also, web tests
 * can't import from shared, so tested FE logic
 */
const zod_1 = require("zod");
const types_1 = require("../IORepresentation/chatML/types");
const adapters_1 = require("./adapters");
function isSingleChatMlMessage(input) {
    return (input !== null &&
        typeof input === "object" &&
        !Array.isArray(input) &&
        typeof input.role === "string" &&
        ("content" in input || "tool_calls" in input));
}
function mapToChatMl(input) {
    let result = types_1.ChatMlArraySchema.safeParse(input);
    if (result.success) {
        return result;
    }
    // Check if input is an array of length 1 including an array of ChatMlMessageSchema
    // e.g. [[ChatMlMessageSchema, ...]]
    const inputArray = zod_1.z.array(types_1.ChatMlArraySchema).safeParse(input);
    if (inputArray.success && inputArray.data.length === 1) {
        return types_1.ChatMlArraySchema.safeParse(inputArray.data[0]);
    }
    // Check if input is an object with a messages key
    // e.g. { messages: [ChatMlMessageSchema, ...] }
    const inputObject = zod_1.z
        .object({
        messages: types_1.ChatMlArraySchema,
    })
        .safeParse(input);
    if (inputObject.success) {
        return types_1.ChatMlArraySchema.safeParse(inputObject.data.messages);
    }
    // Single message object, e.g. { role: "user", content: "..." }
    if (isSingleChatMlMessage(input)) {
        return types_1.ChatMlArraySchema.safeParse([input]);
    }
    return result;
}
function mapOutputToChatMl(output) {
    // Check if output has messages key (LangGraph/LangChain format)
    if (output &&
        typeof output === "object" &&
        !Array.isArray(output) &&
        "messages" in output) {
        const obj = output;
        return types_1.ChatMlArraySchema.safeParse(obj.messages);
    }
    const result = types_1.ChatMlArraySchema.safeParse(Array.isArray(output) ? output : [output]);
    return result;
}
function cleanLegacyOutput(output, fallback) {
    const outLegacyCompletionSchema = zod_1.z
        .object({
        completion: zod_1.z.string(),
    })
        .refine((value) => Object.keys(value).length === 1);
    const outLegacyCompletionSchemaParsed = outLegacyCompletionSchema.safeParse(output);
    const outputClean = outLegacyCompletionSchemaParsed.success
        ? outLegacyCompletionSchemaParsed.data
        : (fallback ?? null);
    return outputClean;
}
function extractAdditionalInput(input) {
    if (isSingleChatMlMessage(input))
        return undefined;
    const adapter = (0, adapters_1.selectAdapter)({ metadata: input, data: input });
    const consumedInputKeys = new Set(["messages"].concat(adapter.getConsumedInputKeys?.(input) ?? []));
    const additionalInput = typeof input === "object" && input !== null && !Array.isArray(input)
        ? Object.fromEntries(Object.entries(input).filter(([key]) => !consumedInputKeys.has(key)))
        : undefined;
    return additionalInput;
}
function combineInputOutputMessages(inputResult, outputResult, cleanOutput) {
    if (!inputResult.success) {
        return [];
    }
    const combinedMessages = [
        ...inputResult.data,
        ...(outputResult.success
            ? outputResult.data.map((m) => ({
                ...m,
                role: m.role ?? "assistant",
            }))
            : [
                {
                    role: "assistant",
                    ...(typeof cleanOutput === "string"
                        ? { content: cleanOutput }
                        : { json: cleanOutput }),
                },
            ]),
    ];
    return combinedMessages;
}
/**
 * Normalize input using shared adapters with frontend schema validation.
 * Uses adapters from shared package but validates with strict frontend schema.
 */
function normalizeInput(input, ctx = {}) {
    const adapter = (0, adapters_1.selectAdapter)({
        ...ctx,
        metadata: ctx.metadata ?? input,
        data: input,
    });
    const preprocessed = adapter.preprocess(input, "input", ctx);
    return mapToChatMl(preprocessed);
}
/**
 * Normalize output using shared adapters with frontend schema validation.
 * Uses adapters from shared package but validates with strict frontend schema.
 */
function normalizeOutput(output, ctx = {}) {
    const adapter = (0, adapters_1.selectAdapter)({
        ...ctx,
        metadata: ctx.metadata ?? output,
        data: output,
    });
    const preprocessed = adapter.preprocess(output, "output", ctx);
    return mapOutputToChatMl(preprocessed);
}
